import React, { Fragment, useState, useEffect, useReducer, useRef } from 'react';
import Head from 'next/head';
import Link from 'next/link';

import Input from '../components/fields/input';
import Loader from '../images/svg-loader';
import { convertParamsToObject, convertToClassNameString } from '../helpers/general';
import LoginStyles from '../styles/login';

import loadAuth from '../apis/auth';
import { FormController, FieldType } from '../utils/FormController';

let auth = null;

const Login = () => {
    const emailInput = useRef(null);
    const passwordInput = useRef(null);
    const [settings, setSettings] = useState({
        loading: true,
        status: null,
        showAccountExpired: false,
        showElevateUserWarning: false,
        mode: null,
        oobCode: null,
    });
    const [loginForm, setLoginForm] = useState(
        new FormController({
            email: {
                type: FieldType.EMAIL,
                validationRules: {
                    required: true,
                },
            },
            password: {
                type: FieldType.PASSWORD,
                validationRules: {
                    required: true,
                },
            },
        })
    );
    const pageCopy =
        settings.mode === 'resetPassword'
            ? {
                  heading: 'Reset your password',
                  message: 'Enter a new password to reset the password on your account',
                  buttonCTA: 'Set New Password',
              }
            : {
                  heading: 'Enter your details below to log in',
                  message: null,
                  buttonCTA: 'Log in',
              };
    const [loginError, dispatchError] = useReducer(
        (state, { code }) => {
            switch (code) {
                case 'auth/weak-password':
                    return {
                        ref: 'password',
                        message: `Your password must have more then 6 characters.`,
                    };
                case 'auth/email-already-in-use':
                    return {
                        ref: 'email',
                        message: `This email is already in use. Please ${(
                            <Link href="/login">
                                <a>login</a>
                            </Link>
                        )} instead.`,
                    };
                case 'auth/too-many-requests':
                    return {
                        ref: 'password',
                        message: `You've made too many incorrect attempts. Please try again later.`,
                    };
                case 'auth/user-not-found':
                case 'auth/wrong-password':
                    return {
                        ref: 'password',
                        message: `Invalid email or password.`,
                    };
                case 'auth/invalid-email':
                    return {
                        ref: 'email',
                        message: `The email provided does not match the sign up email address.`,
                    };
                default:
                    return {
                        ref: 'email',
                        message: `There was an issue when trying to sign you in. Please ${(
                            <a href="https://jclarkefitness.com/contact">login</a>
                        )} if this problem persists.`,
                    };
                // case 'auth/provider-already-linked':
                //     break;
                // case 'auth/user-token-expired':
                //     break;
            }
        },
        { ref: null, message: null }
    );

    const initLoginPage = async () => {
        if (!auth) {
            auth = await loadAuth();
        }

        const { mode, oobCode, accountExpired, elevateUser } = convertParamsToObject(
            window.location
        );

        if (accountExpired === 'true') {
            setSettings({
                ...settings,
                loading: false,
                showAccountExpired: true,
            });
        } else if (elevateUser === 'true') {
            setSettings({
                ...settings,
                loading: false,
                showElevateUserWarning: true,
            });
        } else if (mode === 'resetPassword') {
            try {
                const email = await auth.verifyPasswordResetCode(oobCode);
                setLoginForm(loginForm.handleChange('email', email));
                setSettings({
                    ...settings,
                    loading: false,
                    mode,
                    oobCode,
                });
            } catch (error) {
                const { code } = error;

                switch (code) {
                    case 'auth/expired-action-code':
                        setSettings({
                            ...settings,
                            loading: false,
                            status: 'expiredResetCode',
                        });
                        break;
                    default:
                        setSettings({
                            ...settings,
                            loading: false,
                        });
                        break;
                }
            }
        } else if (mode === 'signIn' && !auth.isSignInWithEmailLink(window.location.href)) {
            setSettings({
                ...settings,
                loading: false,
                mode: null,
                oobCode,
            });
        } else {
            setSettings({
                ...settings,
                loading: false,
                mode,
                oobCode,
            });
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const { mode, oobCode } = settings;
        const validatedLoginForm = new FormController({
            email: {
                ...loginForm.formObject.email,
                value: emailInput.current.value,
            },
            password: {
                ...loginForm.formObject.password,
                value: passwordInput.current.value,
            },
        }).validate();

        if (validatedLoginForm.isValid) {
            console.log('validatedLoginForm is valid');
            const { password, email } = validatedLoginForm.formObject;

            try {
                switch (mode) {
                    case 'resetPassword':
                        await auth.confirmPasswordReset(oobCode, password.value);
                        setSettings({
                            ...settings,
                            status: 'passwordResetSuccess',
                        });
                        break;
                    default:
                        await auth.signInWithEmailAndPassword(email.value, password.value);
                        break;
                }
            } catch (error) {
                dispatchError(error);
            }
        } else {
            setLoginForm(validatedLoginForm);
        }
    };

    useEffect(() => {
        initLoginPage();
    }, []);

    const emailValidationMessage =
        loginForm.formObject.email.errorMessage ||
        (loginError.ref === 'email' && loginError.message);
    const passwordValidationMessage =
        loginForm.formObject.password.errorMessage ||
        (loginError.ref === 'password' && loginError.message);

    return (
        <Fragment>
            <Head>
                <title>Log In | J Clarke Fitness</title>
                <meta
                    name="description"
                    content="Welcome to J Clarke Fitness. Login to your account and view your meal plan, workouts, recipes and much more."
                />
            </Head>
            <div className="content">
                <a className="small-grey-link" href="https://jclarkefitness.com/">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="12"
                        width="12"
                        viewBox="0 0 24 24"
                    >
                        <path d="m0 0h24v24h-24z" fill="none" />
                        <path d="m20 11h-12.17l5.59-5.59-1.42-1.41-8 8 8 8 1.41-1.41-5.58-5.59h12.17z" />
                    </svg>
                    Back To Homepage
                </a>
                <div>
                    <svg
                        className="logo"
                        xmlns="http://www.w3.org/2000/svg"
                        width="249"
                        height="90"
                        viewBox="0 0 166 60"
                    >
                        <title>J Clarke Fitness</title>
                        <g fill="none" fillRule="evenodd">
                            <path
                                fill="#263238"
                                d="M76.239508 22.603457c0 .824856-.102125 1.575144-.306564 2.251054-.20463.67591-.513286 1.255591-.925588 1.73885-.412302.483259-.930532.858403-1.55393 1.125623-.623778.266837-1.358048.400447-2.203002.400447-.382254 0-.759754-.02032-1.131738-.060958-.372175-.04064-.759564-.108115-1.161787-.202812l.181048-1.997444c.013312-.17559.081966-.317636.20615-.42575.124186-.108116.296866-.162365.518231-.162365.113725 0 .254646.018786.422382.055783.167355.037188.368751.055783.603429.055783.328624 0 .61522-.045432.860167-.13687.244757-.091245.447485-.243258.608374-.45623.161079-.21297.28165-.491693.362095-.836357.080635-.344856.120762-.770799.120762-1.277636v-9.490543h3.399971v9.419425zm18.15645 1.531112c.080444 0 .160889.015335.241523.045623.080255.030479.157466.083003.231065.157188l1.347969 1.429649c-.590117.777508-1.329522 1.363706-2.218026 1.75917-.888503.395462-1.939798.59329-3.153502.59329-1.113102 0-2.110766-.190927-2.992424-.572971-.882037-.381853-1.62981-.909202-2.243128-1.581853-.6137-.67246-1.084956-1.470288-1.41358-2.392716-.328435-.922812-.492747-1.924792-.492747-3.006518 0-1.101661.187704-2.113802.563302-3.036613.375408-.92262.900485-1.71853 1.574279-2.387732.673985-.669201 1.480332-1.189648 2.419042-1.561533.93909-.371502 1.97498-.557636 3.10824-.557636.54999 0 1.06784.049265 1.55431.147029.486091.097955.940422.233099 1.362994.405431.422572.172524.811482.378786 1.166922.618594.355439.24.67037.505496.945366.796102l-1.146573 1.551183c-.073979.094696-.16108.180958-.261683.258402-.100413.07802-.241333.116741-.422381.116741-.120572 0-.234868-.026837-.341937-.081086-.10745-.054057-.221365-.12-.341937-.197827-.120762-.077636-.253315-.162173-.397468-.253419-.144344-.091246-.315312-.175783-.512905-.253418-.197974-.077828-.429228-.143579-.694334-.197828-.264725-.054057-.574903-.081086-.930342-.081086-.616932 0-1.180234.110032-1.689906.32952-.509673.219681-.947458.534058-1.312787.94294-.365328.409265-.650403.904217-.855032 1.485431-.20463.581598-.306754 1.233738-.306754 1.956805 0 .75738.102124 1.42984.306754 2.017956.20463.588115.483047 1.083258.835064 1.48524.352016.402363.762606.708306 1.232342.917635.468974.209712.97218.314377 1.508668.314377.308276 0 .589927-.015335.845143-.045623.254646-.03048.491035-.08262.708977-.157189.217942-.074185.425615-.170607.623588-.288881.197593-.118275.397468-.265304.598675-.441087.080444-.067667.167545-.123258.261492-.167348.093757-.043898.191127-.065942.29173-.065942m7.777163 1.08623V13.183035h-3.401302v14.774952h7.766988l1.053576-2.737188zm16.274009-2.911342l-1.348159-4.045495c-.087101-.25687-.189416-.55917-.306564-.907476-.117339-.348115-.232966-.724984-.347072-1.130607-.10745.412332-.218132.794377-.331858 1.14575-.114296.351566-.221365.655783-.322158.91246l-1.347779 4.025368h4.00359zm5.411655 5.647668h-2.635462c-.294963 0-.538199-.069202-.729136-.207988-.190937-.138402-.32368-.31591-.397468-.53214l-.865112-2.5754h-5.572544l-.865112 2.5754c-.067132.189201-.197973.36-.392334.51182-.19474.152205-.43246.228308-.714302.228308h-2.655431l5.673148-14.773036h3.480606l5.673147 14.773036zm5.593122-7.827528h1.367937c.462509 0 .856364-.059233 1.181946-.177508.325201-.118083.591828-.282172.799501-.491693.207862-.20952.358862-.454505.452809-.735144.093948-.280447.140731-.58313.140731-.907476 0-.648881-.209574-1.15591-.628342-1.520894-.419529-.364984-1.06803-.547476-1.946645-.547476h-1.367937v4.380191zm0 2.362429v5.464984h-3.399971V13.184166h4.767908c1.059663 0 1.963.110032 2.710773.32952.747773.219681 1.358238.525624 1.830826.917637.472779.392012.816427.855143 1.031136 1.389201.214519.533866.321778 1.11508.321778 1.743834 0 .48-.063709.93278-.190937 1.35853-.127418.425943-.317024.81968-.568437 1.181406-.251603.361534-.559878.684345-.925397.968243-.365709.283897-.786379.516997-1.26258.69968.228021.114824.441018.25687.638992.425751.197593.169073.370273.375144.51766.618594l3.11851 5.140448h-3.078193c-.569958 0-.978837-.216039-1.227017-.64869l-2.434256-4.289138c-.10745-.189201-.232966-.324345-.37731-.405623-.144153-.080894-.346881-.121533-.608373-.121533h-.865112zm14.384113-3.325764h.543144c.502826 0 .865111-.158722 1.086286-.476358l3.299368-4.826453c.181238-.263579.387199-.44262.618834-.537317.231444-.094696.514426-.142044.849897-.142044h2.957241l-4.345337 5.972204c-.154614.21623-.315312.395271-.482858.537316-.167735.142045-.338704.26032-.513095.354824.254836.094697.487992.224665.699088.39048.211476.165623.404124.386837.578516.664025l4.426162 6.854186h-3.038066c-.19436 0-.360574-.013419-.497881-.04064-.137498-.026836-.256738-.067475-.357151-.121725-.100603-.053865-.187704-.119808-.261493-.197635-.073978-.077636-.144343-.167349-.211095-.268754l-3.319527-5.140448c-.120761-.189201-.273283-.322811-.457944-.400639-.184281-.077635-.434172-.11674-.749104-.11674h-.824985v6.28658h-3.399971V13.18409h3.399971v5.982172zm20.394748-3.344799l1.011928-2.635591v-.002875h-9.437686v14.775144h9.437686v-2.638659h-6.015845v-3.517955h2.706778l.973512-2.533802h-3.68029v-3.446262zM68.977723 32.310748v14.347476h1.0357v-6.590416h4.514214l.332428-.861469h-4.846642v-6.034122h7.16451l.329385-.858402v-.003067zM84.458792 46.65853h1.03608V32.310671h-1.03608zm18.539484-14.347782v.882172h-4.999543v13.465687h-1.026191V33.19292H91.94295v-.882172zm6.790072.035559c.053439.02377.110492.07591.170778.156996l9.21499 12.431502c-.020539-.202811-.030428-.398914-.030428-.588115V32.310652h.904858v14.34786h-.492746c-.154043 0-.278228-.067477-.371985-.202812l-9.254927-12.45163c.020349.202812.030238.395463.030238.577956V46.65851h-.89516V32.31065h.502826c.093947 0 .167736.011886.221556.035656m19.121993 13.450161v-5.98294h4.609111l.32349-.840767h-4.932601v-5.800447h7.16451l.329195-.861661h-8.529405v14.347476h8.532447v-.861661zm21.346922-11.741579c-.060286.115016-.147577.17214-.261492.17214-.087481 0-.200256-.062491-.338134-.187475-.138068-.124793-.32463-.263579-.560449-.415783-.235248-.152013-.529641-.29214-.882608-.420575-.353538-.128435-.789041-.192652-1.306891-.192652-.51823 0-.974083.074185-1.367557.22294-.393475.148562-.723241.351565-.989107.608434-.265676.256869-.467834.554185-.605331.892332-.138068.337955-.206912.69278-.206912 1.064473 0 .48671.102315.888882.307135 1.20671.204629.317827.47506.588114.811101.811054.335471.223322.716775.41099 1.14315.563003.426375.152013.864541.302684 1.314688.451054.449957.148754.887933.312844 1.314308.491885.426756.179233.807869.40409 1.14334.674185.336041.27048.606282.60345.811102.998914.204819.395463.307515.887157.307515 1.475272 0 .601725-.102315 1.167795-.306945 1.698402-.20463.530799-.501495.992013-.890405 1.384026-.38891.392013-.865112.701406-1.428414.927795-.563302.22639-1.206858.339873-1.93124.339873-.9389 0-1.746959-.16754-2.424366-.501854-.677218-.334888-1.271138-.792651-1.78043-1.374057l.28127-.446454c.080635-.101214.174582-.152012.281841-.152012.060476 0 .137498.040639.231825.121725.093947.081086.208053.18115.342318.299233.134454.118275.295533.246901.483618.385495.187894.138403.406216.266837.654967.38492.24818.118467.533634.218147.855983.299234.322349.081278.688438.121725 1.098268.121725.563872 0 1.067649-.086262 1.51076-.258594.443491-.172333.817948-.407157 1.123371-.704665.305423-.297508.538769-.650415.699849-1.059489.161269-.409073.241904-.8469.241904-1.31329 0-.506838-.102315-.924346-.307325-1.252141-.20501-.327796-.475441-.601725-.810912-.821406-.335851-.21968-.717154-.403898-1.143339-.552652-.426375-.14837-.864541-.294057-1.314308-.43591-.450147-.142045-.888504-.300959-1.314689-.47655-.426565-.175782-.807868-.400639-1.143529-.674377-.335852-.273738-.606092-.614952-.811292-1.024025-.20463-.409074-.306754-.921087-.306754-1.53623 0-.48.090523-.943131.271761-1.389202.181429-.44607.446724-.839808.795888-1.181405.349164-.341023.780483-.614952 1.294339-.821023.513857-.206262 1.0996-.309393 1.75818-.309393.738454 0 1.401599.118467 1.989434.354825.587454.23674 1.1228.598274 1.606038 1.084984l-.241524.466581zm14.113531 0c-.060286.115016-.147577.17214-.261492.17214-.087482 0-.200256-.062491-.338134-.187475-.138068-.124793-.324631-.263579-.560449-.415783-.235248-.152013-.529831-.29214-.882608-.420575-.353538-.128435-.789041-.192652-1.306891-.192652-.518231 0-.974083.074185-1.367558.22294-.393474.148562-.72324.351565-.989106.608434-.265677.256869-.467834.554185-.605331.892332-.138068.337955-.206912.69278-.206912 1.064473 0 .48671.102315.888882.307135 1.20671.204629.317827.47506.588114.810911.811054.335661.223322.716964.41099 1.14334.563003a57.38128 57.38128 0 0 0 1.314688.451054c.449957.148754.887933.312844 1.314308.491885.426756.179233.807869.40409 1.14334.674185.336041.27048.606091.60345.811101.998914.20482.395463.307515.887157.307515 1.475272 0 .601725-.102314 1.167795-.306944 1.698402-.20463.530799-.501495.992013-.890405 1.384026-.388911.392013-.865112.701406-1.428414.927795-.563302.22639-1.206859.339873-1.93124.339873-.93909 0-1.747149-.16754-2.424557-.501854-.677217-.334888-1.270948-.792651-1.78024-1.374057l.281271-.446454c.080635-.101214.174582-.152012.281841-.152012.060476 0 .137498.040639.231635.121725.094137.081086.208243.18115.342317.299233.134645.118275.295724.246901.483808.385495.187704.138403.406027.266837.654777.38492.24837.118467.533825.218147.856174.299234.322348.081278.688438.121725 1.098077.121725.564063 0 1.06784-.086262 1.510951-.258594.4433-.172333.817948-.407157 1.123181-.704665.305613-.297508.538959-.650415.700038-1.059489.16127-.409073.241905-.8469.241905-1.31329 0-.506838-.102315-.924346-.307325-1.252141-.20501-.327796-.475441-.601725-.810912-.821406-.336041-.21968-.717154-.403898-1.143339-.552652a47.182195 47.182195 0 0 0-1.314499-.43591c-.449957-.142045-.888313-.300959-1.314498-.47655-.426755-.175782-.807869-.400639-1.14353-.674377-.335851-.273738-.606281-.614952-.811291-1.024025-.20463-.409074-.306945-.921087-.306945-1.53623 0-.48.090524-.943131.271952-1.389202.181428-.44607.446724-.839808.795888-1.181405.349163-.341023.780483-.614952 1.294339-.821023.513856-.206262 1.099599-.309393 1.75818-.309393.738454 0 1.401599.118467 1.989243.354825.587645.23674 1.122991.598274 1.606229 1.084984l-.241524.466581z"
                            />
                            <path
                                fill="#F44336"
                                d="M59.368992 29.92131c0 16.525112-13.290276 29.921406-29.68442 29.921406-16.394333 0-29.68461-13.396294-29.68461-29.921406S13.290239.000096 29.684572.000096c16.394144 0 29.68442 13.396102 29.68442 29.921214"
                            />
                            <path
                                fill="#FFF"
                                d="M42.903704 38.39607c-.962482 1.26901-2.16801 2.22556-3.617342 2.869649-1.448953.64409-3.164343.968243-5.144078.968243-1.814281 0-3.44219-.312652-4.879922-.935655-.62587-.272205-1.211613-.588499-1.755327-.946965.652305-.86262 1.15437-1.86901 1.494595-2.996166.232206-.766773.393855-1.589138.483048-2.457508.167545.260703.350114.504153.547898.728434.574332.657508 1.245654 1.15591 2.010163 1.497125.76641.341214 1.58588.51182 2.46088.51182.503777 0 .962292-.02492 1.378778-.072843.414584-.04984.800642-.136102 1.156272-.256869.35563-.120766.694143-.277955 1.017443-.471565.321397-.19361.6485-.433227.975604-.71885.131221-.11099.273853-.201278.425994-.274121.153853-.070927.31189-.107349.477343-.107349.131221 0 .262443.02492.393665.07476.131031.049841.256738.136103.376358.25687l2.198628 2.33099zm.294736-17.578313l-1.869433 2.530352c-.119811.155271-.262443.295207-.425995.421725-.165263.126517-.393665.189776-.69034.189776-.195691 0-.382254-.04409-.557216-.132268-.174962-.08818-.361335-.195527-.557217-.322045-.197783-.126517-.414584-.264537-.6485-.414057-.23582-.149521-.513477-.285623-.836776-.412141-.3233-.128435-.699849-.235783-1.131549-.323962-.4317-.088178-.937569-.132268-1.517606-.132268-1.006033 0-1.926486.180192-2.757556.538658-.83107.358467-1.54423.870288-2.139483 1.53738-.178765.199361-.34612.41214-.498261.638339v-6.270287c.117909-.04984.23772-.099681.357531-.147604 1.53301-.605751 3.221586-.910543 5.070099-.910543.89573 0 1.742014.08051 2.53505.241533.793034.159105 1.53282.379553 2.22316.661342.688437.279872 1.323626.617252 1.901762 1.008307.580037.391054 1.093513.82428 1.54233 1.297763M28.047668 33.236818c0 1.345495-.166784 2.568882-.500354 3.671118-.333759 1.10262-.836585 2.048243-1.509238 2.836294-.672654.788435-1.517607 1.400128-2.53467 1.835463-1.017252.435336-2.214792.653483-3.592999.653483-.623398 0-1.238808-.033163-1.84566-.09949-.607043-.066325-1.238618-.176357-1.894726-.33067l.295153-3.257827c.02168-.28639.133884-.517956.336232-.694505.202537-.176358.484188-.264729.845143-.264729.185612 0 .415345.030671.689008.091055.273094.060766.601338.091054.984163.091054.535916 0 1.00337-.074377 1.40274-.223131.39918-.149138.730086-.396997.99253-.744345.262633-.347348.459465-.801853.590687-1.36409.131412-.56262.196832-1.256932.196832-2.083897V17.874134h5.54516v15.362684z"
                            />
                        </g>
                    </svg>
                    {settings.loading && (
                        <div className="page-loader">
                            <Loader size={50} color="#677074" />
                        </div>
                    )}
                    {!settings.loading && !settings.status && (
                        <div className="shadow-xl">
                            <h1
                                className={convertToClassNameString([
                                    pageCopy.message ||
                                    settings.showAccountExpired ||
                                    settings.status === 'passwordResetSuccess'
                                        ? 'no-margin'
                                        : '',
                                ])}
                            >
                                {pageCopy.heading}
                            </h1>
                            {settings.showElevateUserWarning && (
                                <p className="account-expired">
                                    This is Elevate account. Please try to log in using the Elevate
                                    app.
                                </p>
                            )}
                            {settings.showAccountExpired && (
                                <p className="account-expired">Your account has expired.</p>
                            )}
                            {pageCopy.message !== null && <p>{pageCopy.message}</p>}
                            <form onSubmit={handleSubmit} method="post">
                                <Input
                                    ref={emailInput}
                                    type={FieldType.EMAIL}
                                    label="Email"
                                    value={loginForm.formObject.email.value}
                                    required
                                    onChange={(value) =>
                                        setLoginForm(loginForm.handleChange('email', value))
                                    }
                                    readOnly={settings.mode === 'resetPassword'}
                                    validationMessage={emailValidationMessage}
                                />
                                <Input
                                    ref={passwordInput}
                                    type={FieldType.PASSWORD}
                                    label="Password"
                                    value={loginForm.formObject.password.value}
                                    required
                                    onChange={(value) =>
                                        setLoginForm(loginForm.handleChange('password', value))
                                    }
                                    validationMessage={passwordValidationMessage}
                                />
                                {!settings.mode && (
                                    <Link href="/request-password-reset">
                                        <a className="fp-link">Forgot Password?</a>
                                    </Link>
                                )}
                                <input type="submit" value={pageCopy.buttonCTA} />
                            </form>
                        </div>
                    )}
                    {settings.status === 'passwordResetSuccess' && (
                        <div>
                            <h1>Password updated successfully</h1>
                            <p>
                                Please go back to the <a href="/login">login</a> page, to login with
                                your new password.
                            </p>
                        </div>
                    )}
                    {settings.status === 'expiredResetCode' && (
                        <div>
                            <h1>Reset Link Expired</h1>
                            <p>
                                This password reset link has expired. If you'd like to request
                                another, please go to{' '}
                                <Link href="/request-password-reset">
                                    <a>this page</a>
                                </Link>
                                .
                            </p>
                        </div>
                    )}
                </div>
            </div>

            <style jsx>{LoginStyles}</style>
        </Fragment>
    );
};

export default Login;
